import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowRight, Loader2, Gift } from "lucide-react";
import { US_STATES } from "@/components/auth/signupConstants";
import { PREREGISTER_OPEN_EVENT } from "@/lib/preRegisterModal";
import { usePreRegisterShare } from "@/components/preregister/usePreRegisterShare";
import { usePreRegisterSubmit } from "@/components/preregister/usePreRegisterSubmit";
import { PreRegisterSuccess } from "@/components/preregister/PreRegisterSuccess";
import { PreRegisterFormFields } from "@/components/preregister/PreRegisterFormFields";
import { SEGMENTS } from "@/components/preregister/preRegisterConstants";

const INITIAL_FORM = { name: "", email: "", phone: "", city: "", state: "", company: "", usdot: "", fleetSize: "", operatingArea: "", ageConfirmed: false };

// Global pre-launch waitlist modal — any CTA on the site can open it via
// openPreRegister(segment) without prop drilling (mounted once in Header.jsx).
export const PreRegisterModal = () => {
  const [open, setOpen] = useState(false);
  const [segment, setSegment] = useState("driver");
  const [form, setForm] = useState(INITIAL_FORM);
  const [done, setDone] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [referredBy, setReferredBy] = useState(null);

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) setReferredBy(ref);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      setSegment(e.detail?.segment || "driver");
      setForm({ ...INITIAL_FORM, state: e.detail?.presetState || "" });
      setDone(false);
      setOpen(true);
    };
    window.addEventListener(PREREGISTER_OPEN_EVENT, handler);
    return () => window.removeEventListener(PREREGISTER_OPEN_EVENT, handler);
  }, []);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const { referralLink, copyReferralLink, shareTwitter, shareSMS, shareEmail } = usePreRegisterShare(referralCode);
  const { submitting, handleSubmit } = usePreRegisterSubmit({
    segment,
    form,
    referredBy,
    onSuccess: (code) => {
      setReferralCode(code);
      setDone(true);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="preregister-modal" className="max-w-md rounded-2xl max-h-[88vh] overflow-y-auto">
        {done ? (
          <PreRegisterSuccess
            referralLink={referralLink}
            onCopy={() => copyReferralLink(() => toast.success("Referral message + link copied!"))}
            onShareTwitter={shareTwitter}
            onShareSMS={shareSMS}
            onShareEmail={shareEmail}
          />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle data-testid="preregister-title" className="font-heading text-2xl text-rr-navy">RelayRigs Is Coming</DialogTitle>
              <DialogDescription>Join the RelayRigs network before launch — tell us how you'll use it.</DialogDescription>
            </DialogHeader>

            {referredBy && (
              <div data-testid="preregister-referred-by-banner" className="bg-rr-surface border border-rr-line rounded-lg px-4 py-2.5 text-xs text-rr-muted flex items-center gap-2">
                <Gift size={14} className="text-rr-orange" /> You were invited by a referral link.
              </div>
            )}

            <form onSubmit={handleSubmit} data-testid="preregister-form" className="space-y-3">
              <select
                data-testid="preregister-segment-select"
                value={segment}
                onChange={(e) => setSegment(e.target.value)}
                className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm bg-white focus:outline-none focus:border-rr-orange font-semibold"
              >
                {SEGMENTS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>

              <input required data-testid="preregister-name-input" placeholder="Full name" value={form.name} onChange={update("name")} className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange" />
              <input required type="email" data-testid="preregister-email-input" placeholder="Email" value={form.email} onChange={update("email")} className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange" />
              <input type="tel" data-testid="preregister-phone-input" placeholder="Phone (optional)" value={form.phone} onChange={update("phone")} className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange" />

              <div className="grid grid-cols-2 gap-3">
                <input data-testid="preregister-city-input" placeholder="City" value={form.city} onChange={update("city")} className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange" />
                <select data-testid="preregister-state-select" value={form.state} onChange={update("state")} className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm bg-white focus:outline-none focus:border-rr-orange">
                  <option value="">State</option>
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <PreRegisterFormFields segment={segment} form={form} update={update} />

              <label className="flex items-start gap-2.5 text-xs text-rr-muted cursor-pointer pt-1">
                <input
                  required
                  type="checkbox"
                  data-testid="preregister-age-checkbox"
                  checked={form.ageConfirmed}
                  onChange={(e) => setForm((f) => ({ ...f, ageConfirmed: e.target.checked }))}
                  className="mt-0.5 accent-rr-orange"
                />
                I confirm that I am at least 18 years old.
              </label>

              <button type="submit" disabled={submitting || !form.ageConfirmed} data-testid="preregister-submit-button" className="w-full bg-rr-orange text-white font-bold py-3.5 rounded-full hover:bg-rr-navy transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {submitting ? <Loader2 size={18} className="animate-spin" /> : <>Pre-Register for RelayRigs <ArrowRight size={16} /></>}
              </button>
              <p className="text-xs text-rr-muted text-center">
                Pricing and included features are subject to final launch terms. Pre-registration does not automatically begin a paid subscription.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

